import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  commissions,
  operations,
  quoteFinancial,
  type EndorsementDetail,
  type PolicyDetail,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import {
  formatCivilDate,
  formatMoney,
  formatRate,
  moneyInputError,
  parseRate,
  toMoney,
} from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { MoneyField } from '@/shared/ui/MoneyField';
import { useToast } from '@/shared/ui/useToast';

import { regrasEmTexto } from './endossoFinanceiro';
import { EndorsementInfo } from './EndorsementPolicyDetails';

/**
 * "Dados financeiros" e "Parcelas" do endosso com efeito financeiro (P-END-2; END-109
 * a END-119), e a comissao ligada ao endosso (decisao 6 da 11.7 do plano 49).
 *
 * Decisao 2a: taxa, comissao, premio, parcelas e forma de pagamento sao os da operacao
 * do tipo endosso (P-COT-4). O premio que a operacao deriva e o da garantia pedida, e
 * nao o premio do endosso: o premio do endosso, o adicional de fracionamento e o valor
 * da comissao do endosso sao regra pendente, e a tela diz qual.
 */
export function EndorsementFinancialPaymentData({
  endorsement,
  policy,
}: {
  endorsement: EndorsementDetail;
  policy: PolicyDetail | null;
}) {
  const [vendoParcelas, setVendoParcelas] = useState(false);
  const financeiroDoEndosso = endorsement.financial ?? null;
  const operacaoId = endorsement.operation?.id ?? '';

  const operacao = useQuery({
    queryKey: ['operations', operacaoId],
    queryFn: ({ signal }) => operations.get(operacaoId, signal),
    enabled: operacaoId !== '',
    retry: false,
  });
  const financeiro = useQuery({
    queryKey: ['operations', operacaoId, 'financial'],
    queryFn: ({ signal }) => quoteFinancial.get(operacaoId, signal),
    enabled: operacaoId !== '',
    retry: false,
  });
  if (!financeiroDoEndosso) return null;

  const parcelas = financeiro.data?.installments.items ?? [];
  const primeira = financeiro.data?.installments.first_installment ?? null;

  return (
    <>
      <Card>
        <CardHeader
          title="Dados financeiros"
          description="Taxa, comissao, parcelas e termo sao os da cotacao de endosso. O premio do endosso e regra pendente."
        />
        <CardBody className="flex flex-col gap-4">
          {operacaoId === '' ? (
            <p className="text-sm text-ink-600">
              Gere a cotacao no resumo da alteracao para ver os dados financeiros da
              cotacao de endosso.
            </p>
          ) : null}
          {operacao.isError || financeiro.isError ? (
            <Alert tone="danger" title="Erro ao carregar cotacao">
              {(operacao.error ?? financeiro.error)?.message}
            </Alert>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <EndorsementInfo
              rotulo="Taxa aplicada (%)"
              valor={
                operacao.data
                  ? `${formatRate(operacao.data.applied_rate_bps)} (taxa de risco aplicada sobre o calculo de premio)`
                  : ''
              }
            />
            <EndorsementInfo
              rotulo="% Comissao"
              valor={operacao.data ? formatRate(operacao.data.commission_bps) : ''}
            />
            <EndorsementInfo
              rotulo="Valor da comissao"
              valor={`Regra pendente (${regrasEmTexto(financeiroDoEndosso.commission_rules)})`}
            />
            <EndorsementInfo
              rotulo={
                financeiro.data?.min_premium
                  ? `Valor do premio (Minimo ${formatMoney(financeiro.data.min_premium)})`
                  : 'Valor do premio'
              }
              valor={`Regra pendente (${regrasEmTexto(financeiroDoEndosso.premium_rules)})`}
            />
            <EndorsementInfo
              rotulo="Forma de pagamento"
              valor={financeiro.data?.payment_method_label || 'Nao informada'}
            />
            <EndorsementInfo
              rotulo="Numero de parcelas"
              valor={
                financeiro.data?.installments_count
                  ? String(financeiro.data.installments_count)
                  : 'Nao informado'
              }
            />
            <EndorsementInfo
              rotulo="Adicional de fracionamento na parcela (R$)"
              valor="Regra pendente (PRC-007, E12)"
            />
            <EndorsementInfo
              rotulo="1a parcela"
              valor={
                primeira
                  ? `${formatMoney(primeira.amount)} em ${formatCivilDate(primeira.due_on)}`
                  : 'Nao informada'
              }
            />
            <EndorsementInfo
              rotulo="Sentido do premio"
              valor={
                financeiroDoEndosso.premium_direction ??
                `Regra pendente (${regrasEmTexto(financeiroDoEndosso.premium_rules)})`
              }
            />
          </div>
          {parcelas.length > 0 ? (
            <div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setVendoParcelas(true)}
              >
                Exibir outras parcelas
              </Button>
            </div>
          ) : null}
        </CardBody>
      </Card>

      <EndorsementCommissionCard endorsement={endorsement} policy={policy} />

      <Modal
        open={vendoParcelas}
        onClose={() => setVendoParcelas(false)}
        title="Parcelas"
        size="sm"
        footer={<Button onClick={() => setVendoParcelas(false)}>Fechar</Button>}
      >
        <ul className="flex flex-col gap-1 text-sm text-ink-900">
          {parcelas.map((parcela) => (
            <li key={parcela.number}>
              {parcela.number}a de {parcelas.length}: {formatMoney(parcela.amount)} em{' '}
              {formatCivilDate(parcela.due_on)}
            </li>
          ))}
        </ul>
      </Modal>
    </>
  );
}

/**
 * Comissao do endosso (END-111, END-112): so a ligacao. O valor e o estorno no
 * cancelamento nao tem regra aprovada; a comissao lancada a mao por quem tem a alcada
 * pode ser ligada ao endosso aplicado.
 */
function EndorsementCommissionCard({
  endorsement,
  policy,
}: {
  endorsement: EndorsementDetail;
  policy: PolicyDetail | null;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [lancando, setLancando] = useState(false);
  const [base, setBase] = useState('');
  const [taxa, setTaxa] = useState('');
  const [competencia, setCompetencia] = useState('');
  const [motivo, setMotivo] = useState('');
  const financeiro = endorsement.financial ?? null;
  const aplicado =
    endorsement.status === 'aprovado' || endorsement.status === 'emitido';
  const corretora = policy?.broker?.id ?? '';
  const pontos = parseRate(taxa);

  const lancar = useMutation({
    mutationFn: () =>
      commissions.launch({
        broker_id: corretora,
        base: toMoney(base),
        rate_bps: pontos ?? 0,
        ...(competencia ? { competence: competencia } : {}),
        manual_reason: motivo,
        endorsement_id: endorsement.id,
      }),
    onSuccess: () => {
      toast.success('Comissao do endosso lancada.');
      setLancando(false);
      void queryClient.invalidateQueries({ queryKey: ['endorsement', endorsement.id] });
      // A tela de comissoes mostra o lancamento novo (achado B49 da revisao das ondas
      // 5 e 6).
      void queryClient.invalidateQueries({ queryKey: ['commissions'] });
    },
  });
  if (!financeiro) return null;

  return (
    <Card>
      <CardHeader
        title="Comissao do endosso"
        description={financeiro.commission_rules.message}
      />
      <CardBody className="flex flex-col gap-3">
        {financeiro.commissions.length === 0 ? (
          <p className="text-sm text-ink-600">Nenhuma comissao ligada ao endosso.</p>
        ) : (
          <ul className="flex flex-col gap-1 text-sm text-ink-900">
            {financeiro.commissions.map((item) => (
              <li key={item.id}>
                {formatMoney(item.amount)} ({formatRate(item.rate_bps)}) - competencia{' '}
                {item.competence} - {item.manual_reason}
              </li>
            ))}
          </ul>
        )}
        {aplicado && corretora && can('comissoes.comissao.create') ? (
          <div>
            <Button variant="secondary" size="sm" onClick={() => setLancando(true)}>
              Lancar comissao do endosso
            </Button>
          </div>
        ) : null}
      </CardBody>
      <Modal
        open={lancando}
        onClose={() => setLancando(false)}
        title="Lancar comissao do endosso"
        description="Lancamento manual ligado ao endosso: o valor e base x percentual, informados por quem tem a alcada."
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setLancando(false)}>
              Cancelar
            </Button>
            <Button
              loading={lancar.isPending}
              disabled={
                base.trim() === '' ||
                moneyInputError(base) !== undefined ||
                pontos === null ||
                motivo.trim() === ''
              }
              onClick={() => lancar.mutate()}
            >
              Lancar
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {lancar.isError ? (
            <Alert tone="danger" title="Nao foi possivel lancar">
              {lancar.error.message}
            </Alert>
          ) : null}
          <MoneyField
            label="Base de calculo (R$)"
            value={base}
            error={moneyInputError(base)}
            onValueChange={setBase}
          />
          <Field
            label="Percentual (%)"
            inputMode="decimal"
            value={taxa}
            onChange={(event) => setTaxa(event.target.value)}
          />
          <Field
            label="Competencia"
            type="month"
            value={competencia}
            onChange={(event) => setCompetencia(event.target.value)}
          />
          <Field
            label="Motivo do lancamento"
            value={motivo}
            onChange={(event) => setMotivo(event.target.value)}
          />
        </div>
      </Modal>
    </Card>
  );
}
